import { forwardRef, useEffect, useMemo, useRef } from 'react';
import type { PropsWithoutRef, MouseEvent } from 'react';

import {
  Avatar,
  Flex,
  Icon,
  registerIcon,
  SummaryItem,
  Image,
  EmojiDisplay,
  useI18n,
  Actions,
  Tooltip,
  useTheme,
  useElement,
  markdownToPlainText
} from '@pega/cosmos-react-core';
import type { FlexContainerProps, ForwardRefForwardPropsComponent } from '@pega/cosmos-react-core';
import * as documentDocIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/document-doc.icon';
import * as chainUpIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/chain-up.icon';
import * as flagWaveSolidIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/flag-wave-solid.icon';
import { RichTextViewer } from '@pega/cosmos-react-rte';

import type { MessageProps, AttachmentItemProps } from './Chat.types';
import {
  StyledMessageContainer,
  StyledMessageMain,
  StyledMessageBubble,
  StyledMessageBubbleContent,
  StyledMediaList,
  StyledMediaListItem,
  StyledMediaThumbNail,
  StyledMediaMessage,
  StyledMediaLink,
  StyledMetaInfoContainer,
  StyledMetaInfo,
  StyledBlinkingDot,
  StyledMediaButton,
  StyledMessageHeader,
  StyledMessageHeaderContent,
  StyledMessageHeaderMeta,
  StyledUndeliveredIcon,
  StyledStatusInfo,
  StyledHeadset,
  getMessageColors,
  StyledFlagIcon,
  StyledHeadsetIconBackground,
  StyledMediaContent
} from './Message.styles';

registerIcon(documentDocIcon, chainUpIcon, flagWaveSolidIcon);

const Attachment = (props: AttachmentItemProps) => {
  const { id, icon, name, meta, actions = [], onPreview, thumbnail } = props;
  const actionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (
        (e.target as HTMLButtonElement)?.getAttribute('aria-expanded') === 'true' &&
        ['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'].includes(e.key)
      ) {
        e.stopPropagation();
      }
    };

    actionRef.current?.addEventListener('keydown', listener);

    return () => {
      actionRef.current?.removeEventListener('keydown', listener);
    };
  }, [actions]);

  return (
    <Flex
      key={id}
      as={StyledMediaListItem}
      container={{
        direction: 'column'
      }}
    >
      {thumbnail && (
        <Image
          as={StyledMediaThumbNail}
          src={thumbnail}
          onClick={(e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onPreview?.(id);
          }}
          alt={name}
        />
      )}

      <SummaryItem
        as={StyledMediaMessage}
        primary={
          <StyledMediaButton
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onPreview?.(id);
            }}
          >
            {name}
          </StyledMediaButton>
        }
        secondary={meta}
        visual={<Icon name={icon || 'document-doc'} />}
        actions={<Actions ref={actionRef} items={actions} iconOnly />}
      />
    </Flex>
  );
};

const Message: ForwardRefForwardPropsComponent<MessageProps> = forwardRef(function Message(
  {
    message,
    direction,
    avatarInfo,
    timestamp,
    attachments = [],
    mediaPageLinks = [],
    status,
    typing,
    messageHeader,
    senderType,
    senderId,
    agentVariant,
    renderMarkdownContent = false,
    ...restProps
  }: PropsWithoutRef<MessageProps>,
  ref: MessageProps['ref']
) {
  const t = useI18n();
  const theme = useTheme();
  const [avatarRef, setAvatarRef] = useElement();
  const { content: messageBubbleBackgroundColor } = useMemo(
    () => getMessageColors(theme, direction, senderType, agentVariant),
    [theme, direction, senderType, agentVariant]
  );

  let messageMetaJustify: FlexContainerProps['justify'] = 'between';
  if (timestamp && !status) {
    messageMetaJustify = 'start';
  } else if (!timestamp && status) {
    messageMetaJustify = 'end';
  }

  const renderAvatar = useMemo(() => {
    if (direction !== 'in' || (senderType !== 'bot' && !avatarInfo)) {
      return null;
    }

    if (senderType === 'agent') {
      return (
        <>
          <StyledHeadset>
            <Avatar
              name={avatarInfo!.name}
              size='m'
              shape='circle'
              imageSrc={avatarInfo!.imageSrc}
              ref={setAvatarRef}
              backgroundColor={messageBubbleBackgroundColor}
            />
            <StyledHeadsetIconBackground>
              <Icon name='headset' />
            </StyledHeadsetIconBackground>
          </StyledHeadset>
          <Tooltip target={avatarRef} showDelay='none' hideDelay='none'>
            {avatarInfo!.name}
          </Tooltip>
        </>
      );
    }

    return (
      <>
        <Avatar
          name={avatarInfo ? avatarInfo.name : t('bot')}
          size='m'
          shape='circle'
          icon={!avatarInfo?.imageSrc && senderType === 'bot' ? 'robot-solid' : undefined}
          imageSrc={avatarInfo?.imageSrc}
          ref={setAvatarRef}
          backgroundColor={messageBubbleBackgroundColor}
        />
        <Tooltip target={avatarRef} showDelay='none' hideDelay='none'>
          {avatarInfo ? avatarInfo.name : t('bot')}
        </Tooltip>
      </>
    );
  }, [avatarInfo, senderType, direction, avatarRef]);

  let ariaLabel: string | undefined;
  const sender = avatarInfo ? avatarInfo.name : senderType;
  const messageContent = renderMarkdownContent ? markdownToPlainText(message ?? '', true) : message;
  ariaLabel = t('sender_replied_message', [sender, messageContent ?? '']);
  if (attachments.length > 0) ariaLabel = t('chat_attachments', [ariaLabel, attachments.length]);
  if (mediaPageLinks.length > 0) ariaLabel = t('chat_links', [ariaLabel, mediaPageLinks.length]);
  if (timestamp) ariaLabel = t('chat_message_at_timestamp', [ariaLabel, timestamp]);
  if (status && direction === 'out') ariaLabel += ` ${t('chat_message_status', [status])}`;

  return (
    <Flex
      as={StyledMessageContainer}
      ref={ref}
      container={{
        gap: 1.5,
        direction: direction === 'in' ? 'row' : 'row-reverse'
      }}
      item={{
        shrink: 0
      }}
      hasMessage={!!message}
      direction={direction}
      senderType={senderType}
      typing={typing}
      messageHeader={messageHeader}
      status={status}
      agentVariant={agentVariant}
      aria-label={ariaLabel}
      {...restProps}
    >
      {renderAvatar}
      <Flex
        container={{
          direction: 'column',
          alignItems: direction === 'in' ? 'start' : 'end',
          justify: 'center'
        }}
        as={StyledMessageMain}
      >
        <Flex
          container={{
            direction: direction === 'in' ? 'row' : 'row-reverse',
            alignItems: 'center',
            gap: 0.5
          }}
        >
          <StyledMessageBubble>
            {messageHeader && (
              <Flex container={{ gap: 0.5, justify: 'between' }} as={StyledMessageHeader}>
                <Flex container={{ gap: 0.5 }}>
                  <StyledFlagIcon name='flag-wave-solid' />
                  <StyledMessageHeaderContent>{messageHeader.content}</StyledMessageHeaderContent>
                </Flex>

                <StyledMessageHeaderMeta>{messageHeader.meta}</StyledMessageHeaderMeta>
              </Flex>
            )}

            {message && (
              <StyledMessageBubbleContent>
                {renderMarkdownContent ? (
                  <RichTextViewer content={message} type='markdown' />
                ) : (
                  <EmojiDisplay content={message} size={20} />
                )}
              </StyledMessageBubbleContent>
            )}

            {(attachments.length > 0 || mediaPageLinks.length > 0) && (
              <StyledMediaList>
                {attachments.map(attachment => {
                  return <Attachment {...attachment} key={attachment.id} />;
                })}

                {mediaPageLinks.map(pagePushLink => {
                  const { id, href, title, thumbnail, onTitleClick } = pagePushLink;
                  return (
                    <Flex
                      as={StyledMediaListItem}
                      key={id}
                      container={{
                        direction: 'column'
                      }}
                    >
                      {thumbnail && <Image as={StyledMediaThumbNail} src={thumbnail} alt={title} />}

                      <Flex container={{ gap: 1, alignItems: 'center' }} as={StyledMediaMessage}>
                        <Icon name='book-open' />
                        <Flex container as={StyledMediaContent}>
                          {href ? (
                            <StyledMediaLink
                              aria-label={title}
                              href={href}
                              target='_blank'
                              rel='noreferrer'
                            >
                              {title}
                            </StyledMediaLink>
                          ) : (
                            <>
                              {onTitleClick && (
                                <StyledMediaButton
                                  variant='link'
                                  onClick={() => {
                                    onTitleClick();
                                  }}
                                >
                                  {title}
                                </StyledMediaButton>
                              )}
                            </>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  );
                })}
              </StyledMediaList>
            )}
          </StyledMessageBubble>

          {status === 'undeliverable' && message && direction === 'out' && (
            <StyledUndeliveredIcon name='warn-solid' />
          )}
        </Flex>

        {!typing && (
          <Flex
            as={StyledMetaInfoContainer}
            container={{
              justify: messageMetaJustify,
              gap: 1.5
            }}
          >
            {timestamp && <StyledMetaInfo>{timestamp}</StyledMetaInfo>}
            {status && direction === 'out' && <StyledStatusInfo>{t(status)}</StyledStatusInfo>}
          </Flex>
        )}

        {typing && (
          <Flex
            container={{
              gap: 0.25
            }}
          >
            <StyledBlinkingDot delay={0} />
            <StyledBlinkingDot delay={0.25} />
            <StyledBlinkingDot delay={0.5} />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

export default Message;
